import { registerAmplitudeEventMapping } from './event-registry';
import { BannerClickEvent, CheckoutCompletedEvent, SubscriptionCTAClickEvent, SubscriptionOfferPresented } from './types/events';
import { RevenueObject } from './types/funnel-event';

// ---------------------------------------------------------------------------
// Payload interfaces
//
// Each interface describes the snake_case fields that the corresponding
// event-tracking event places on the raw event object. These types exist solely
// to give transformers (and enrichers) a typed view of the incoming payload —
// they are not exported because consumers should not depend on them directly.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Default mappings
// ---------------------------------------------------------------------------

/**
 * Registers the default set of event-tracking → Amplitude mappings for all
 * events defined in `@udemy/amplitude`. Call this once at application startup,
 * before any events are published.
 *
 * The callback returned by `createAmplitudeTrackingCallback()` consults this
 * registry on every event; events whose `_type` is not listed here are silently
 * dropped. Add new entries to this array as new Amplitude events are defined.
 */
export function registerDefaultAmplitudeEventMappings() {
  // --- BannerClickEvent ---------------------------------------------------
  // Demonstrates straightforward optional-field mapping.
  registerAmplitudeEventMapping('BannerClickEvent', event => {
    const e = event;
    if (e.notice_id === undefined) {
      return null;
    }
    return new BannerClickEvent(e.notice_id, e.notice_type, e.personalized_notice_set_id, e.personalized_notice_set_name, e.top_membership_target_group_id, e.slide_number, e.url, e.ui_region);
  });

  // --- SubscriptionCTAClickEvent ------------------------------------------
  // Demonstrates mapping required + optional fields including an array.
  registerAmplitudeEventMapping('SubscriptionCTAClickEvent', event => {
    const e = event;
    if (!e.plan_price_id || !e.plan_type || !e.cta_type || !e.ui_region) {
      return null;
    }
    return new SubscriptionCTAClickEvent(e.plan_price_id, e.plan_type, e.cta_type, e.ui_region, e.course_id, e.course_tracking_id, e.plan_price_ids);
  });

  // --- CheckoutCompletedEvent ---------------------------------------------
  // Demonstrates mapping a nested array of revenue objects (RevenueObject[]).
  registerAmplitudeEventMapping('CheckoutCompletedEvent', event => {
    var _e$revenue_objects;
    const e = event;
    if (!e.order_id || !((_e$revenue_objects = e.revenue_objects) !== null && _e$revenue_objects !== void 0 && _e$revenue_objects.length)) {
      return null;
    }
    const revenueObjects = e.revenue_objects.map(item => new RevenueObject(item.product_id, item.price, item.quantity, item.revenue_type, e.currency));
    return new CheckoutCompletedEvent(e.order_id, e.payment_method, revenueObjects, e.currency, e.coupon_code, e.total_value, e.total_discount_value, e.total_items, e.checkout_type);
  });

  // --- SubscriptionOfferPresented -----------------------------------------
  // Demonstrates mapping a complex event with many required fields and
  // optional nullable types.
  registerAmplitudeEventMapping('SubscriptionOfferPresentedEvent', event => {
    const e = event;
    if (!e.price || !e.currency || !e.product_type || !e.country_code || !e.ui_region) {
      return null;
    }
    return new SubscriptionOfferPresented(e.price, e.list_price, e.currency, e.renewal_interval_type, e.renewal_interval_count, e.has_trial, e.product_type, e.country_code, e.course_id, e.course_tracking_id, e.badges, e.ui_region, e.trial_days);
  });
}
//# sourceMappingURL=register-default-mappings.js.map